/**
 * Group union pipeline:
 * 1. Each finished trail is converted to a **closed outline polygon** (same
 *    idea as Illustrator “Outline Stroke” / filled ribbon vs expanded stroke).
 * 2. Those polygons are boolean-unioned with Clipper.
 */

import ClipperLib from "clipper-lib";
import {
    applyEndpointStrokeTaper,
    meanStrokeWidth,
    polylineRibbonOutlineToRing,
    polylineRoundCapStrokeOutlineToRing,
} from "./pathAgents";
import type { Point2 } from "./flowField";

const CLIPPER_SCALE = 100_000;

export interface FlowAgentTrailForUnion {
    points: Point2[];
    strokeWidths?: number[];
    strokeColor: string;
}

export interface FlowAgentsGroupUnionResult {
    /** Single `d` with multiple closed subpaths (`M…Z` repeated), like Illustrator Pathfinder Unite export. */
    pathD: string;
    fill: string;
}

function toClipperPath(points: [number, number][]): unknown[] {
    return points.map(
        ([x, y]) =>
            new ClipperLib.IntPoint2(
                Math.round(x * CLIPPER_SCALE),
                Math.round(y * CLIPPER_SCALE),
            ),
    );
}

function clipperPathToPathD(path: { X: number; Y: number }[]): string {
    if (path.length < 3) return "";
    const fmt = (n: number) => {
        const x = n / CLIPPER_SCALE;
        const s = x.toFixed(6);
        return s.replace(/\.?0+$/, "") || "0";
    };
    let d = `M${fmt(path[0].X)} ${fmt(path[0].Y)}`;
    for (let i = 1; i < path.length; i++) {
        d += `L${fmt(path[i].X)} ${fmt(path[i].Y)}`;
    }
    d += "Z";
    return d;
}

export function buildFlowAgentsGroupUnion(options: {
    trails: FlowAgentTrailForUnion[];
    fallbackStrokeWidth: number;
    mergeTrailToSinglePath: boolean;
    /** If true, outline = filled ribbon (matches on-screen filled trails). Else outline = expanded stroke with round caps (matches stroked trails). */
    filledVariableWidthStroke: boolean;
    /** Fade width over this arc length at trail start/end (user units); 0 = none. */
    trailTaperStartLength?: number;
    trailTaperEndLength?: number;
}): FlowAgentsGroupUnionResult | null {
    const {
        trails,
        fallbackStrokeWidth,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        trailTaperStartLength = 0,
        trailTaperEndLength = 0,
    } = options;
    const taper =
        trailTaperStartLength > 0 || trailTaperEndLength > 0;
    if (trails.length === 0) return null;

    const allPolys: unknown[][] = [];
    let fill = trails[0]?.strokeColor || "#000000";

    for (const t of trails) {
        if (t.points.length < 2) continue;
        fill = t.strokeColor || fill;
        const n = t.points.length;
        const hasPerVertexWidths =
            Array.isArray(t.strokeWidths) && t.strokeWidths.length === n;
        const safeFallback = Math.max(1e-6, fallbackStrokeWidth);
        let halfs: number[];

        if (hasPerVertexWidths) {
            const widths = t.strokeWidths!;
            // Filled ribbon preview always uses per-vertex width; union must match.
            // Mean width only applies to stroked (non-filled) paths when "merge to single path" is on and no taper.
            if (
                mergeTrailToSinglePath &&
                !filledVariableWidthStroke &&
                !taper
            ) {
                const w = Math.max(1e-6, meanStrokeWidth(widths));
                halfs = Array.from({ length: n }, () => w / 2);
            } else {
                let wVert = widths.map((w) => Math.max(1e-6, w));
                if (taper) {
                    wVert = applyEndpointStrokeTaper(
                        t.points,
                        wVert,
                        trailTaperStartLength,
                        trailTaperEndLength,
                    );
                }
                halfs = wVert.map((w) => Math.max(1e-6, w) / 2);
            }
        } else {
            let wVert = Array.from({ length: n }, () => safeFallback);
            if (taper) {
                wVert = applyEndpointStrokeTaper(
                    t.points,
                    wVert,
                    trailTaperStartLength,
                    trailTaperEndLength,
                );
            }
            halfs = wVert.map((w) => Math.max(1e-6, w) / 2);
        }

        const ring = filledVariableWidthStroke
            ? polylineRibbonOutlineToRing(t.points, halfs)
            : polylineRoundCapStrokeOutlineToRing(t.points, halfs);
        if (!ring || ring.length < 3) continue;
        allPolys.push(toClipperPath(ring));
    }

    if (allPolys.length === 0) return null;

    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(allPolys, ClipperLib.PolyType.ptSubject, true);
    const solution = new ClipperLib.Paths();
    const ok = clipper.Execute(
        ClipperLib.ClipType.ctUnion,
        solution,
        ClipperLib.PolyFillType.pftNonZero,
        ClipperLib.PolyFillType.pftNonZero,
    );

    if (!ok || solution.length === 0) return null;

    const pathDs: string[] = [];
    for (let i = 0; i < solution.length; i++) {
        const d = clipperPathToPathD(solution[i]);
        if (d) pathDs.push(d);
    }

    if (pathDs.length === 0) return null;
    const pathD = pathDs.join("");
    return { pathD, fill };
}
